import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { v4 as uuid } from "uuid";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import { client } from "../../hooks/Client";
import { useFirestore, useFirestoreOrderBy } from "../../firebase/useFirestore";
import { db, timestamp } from "../../firebase/config.js";
import ScrollToTop from "../../hooks/ScrollToTop";
import useSettings from "../../hooks/Settings";
import Tooltip from "../../components/common/Tooltip";
import FieldCount from "../../components/Questionnaire/FieldCount";
import DeleteModal from "../../components/common/DeleteModal";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import TableTitleCell from "../../components/common/table/TableTitleCell.jsx";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import notDeleted from "../../helpers/notDeleted";
import softDeleteDoc from "../../helpers/softDeleteDoc";
import buildQuestionnaireCopy from "../../helpers/questionnaire/buildQuestionnaireCopy";

// Firestore: max 500 writes per batch, max 30 values in an `in` filter.
const COPY_BATCH_SIZE = 400;
const FIELD_IN_LIMIT = 30;

/**
 * Tell the user which part of the questionnaire did not come along. A
 * meetstandaard question belongs to one questionnaire at a time, so the copy
 * leaves it out (see buildQuestionnaireCopy).
 * AI-CONTEXT: See docs/features/msi-effect-meerdere-vragen.md
 *
 * @param {string} title - Title of the new copy
 * @param {number} count - Meetstandaard questions left out
 * @returns {string} The message, or '' when nothing was left out
 */
const standardSkippedNotice = (title, count) => {
  if (!count) return "";
  const one = count === 1;
  const what = one ? "de meetstandaardvraag" : `de ${count} meetstandaardvragen`;
  const it = one ? "hem" : "ze";
  return `'${title}' is gemaakt zonder ${what}. Een meetstandaardvraag staat in één vragenlijst tegelijk. Wil je ${it} in de kopie, haal ${it} dan uit het origineel en voeg ${it} toe via 'Gestandaardiseerde effecten toevoegen'.`;
};

const Questionnaires = () => {
  // Context
  const [saved, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);

  // State
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDocid, setDeleteDocid] = useState("");
  const [deleteName, setDeleteName] = useState("");
  const [dragArray, setDragArray] = useState([]);
  const [copyingId, setCopyingId] = useState("");
  const [copyError, setCopyError] = useState("");
  const [copyNotice, setCopyNotice] = useState("");
  // Blocks a second click before the re-render that disables the icon.
  const copyingRef = useRef(false);

  // Hooks
  ScrollToTop();
  const history = useHistory();
  const { subscription } = useSettings();

  // Firestore
  const questionnaires = useFirestoreOrderBy("Questionnaires", "Position", "asc");
  const researches = useFirestore("Research");

  // Map each questionnaire ID -> the titles of the research(es) linked to it
  const researchTitlesByQuestionnaire = useMemo(() => {
    const map = {};
    (researches || []).forEach((research) => {
      if (!research.QuestionnaireID) return;
      if (!map[research.QuestionnaireID]) map[research.QuestionnaireID] = [];
      map[research.QuestionnaireID].push(research.Title || "Naamloos onderzoek");
    });
    return map;
  }, [researches]);

  // Keep a local array for instant UI re-order
  useEffect(() => {
    setDragArray(questionnaires || []);
  }, [questionnaires]);

  // Delete modal
  const deleteModalHandler = (e) => {
    setDeleteDocid(e.currentTarget.dataset.docid);
    setDeleteName(e.currentTarget.dataset.deletename);
    setDeleteModal(true);
  };

  // Zacht (ADR-008): de vragenlijst met haar vragen is terug te zetten via de
  // prullenbak. Tot 2026-09-15 was dit een harde delete — de wezen daarvan zijn
  // 2026-10-02 geteld en opgeruimd (inbox #296).
  // AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans
  const deleteQuestionnaire = async () => {
    await softDeleteDoc({ collection: "Questionnaires", docid: deleteDocid, authId: auth?.ID, title: deleteName });
    setSaved("flex");
    setDeleteModal(false);
  };

  const addQuestionnaire = () => {
    const newId = uuid();
    db.collection("Questionnaires")
      .doc()
      .set({
        ID: newId,
        Timestamp: timestamp,
        Compagny: client,
        CompagnyID: client,
        EvidenceBased: false,
        Position: (questionnaires?.length || 0) + 1,
        Title: "Vragenlijst " + ((questionnaires?.length || 0) + 1),
      });
  };

  const titleHandler = (e) => {
    const docid = e.target.dataset.docid;
    const title = e.target.value;
    db.collection("Questionnaires")
      .doc(docid)
      .update({ Title: title })
      .then(() => setSaved("flex"));
  };

  // Kopiëren: lees de vragenlijst met haar vragen, opties, rijen en kolommen,
  // laat buildQuestionnaireCopy de ID's omzetten en schrijf alles weg. De
  // vragenlijst zelf gaat in de laatste batch, zodat een mislukte eerdere batch
  // geen halve kopie in de lijst zet.
  // AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
  /**
   * Read the live children (options, rows or columns) of the given questions.
   * @param {string} collection - `MultipleQuestionOptions`, `MatrixQuestionRows` or `MatrixQuestionColumns`
   * @param {Array<string>} fieldIds - `QuestionnaireFields.ID`s to read the children of
   * @returns {Promise<Array<object>>} Child docs with their `docid`
   */
  const fetchChildren = async (collection, fieldIds) => {
    const chunks = [];
    for (let i = 0; i < fieldIds.length; i += FIELD_IN_LIMIT) {
      chunks.push(fieldIds.slice(i, i + FIELD_IN_LIMIT));
    }
    const snaps = await Promise.all(
      chunks.map((chunk) =>
        db.collection(collection).where("CompagnyID", "==", client).where("Field", "in", chunk).get()
      )
    );
    return snaps.flatMap((snap) => snap.docs.map((d) => ({ ...d.data(), docid: d.id })));
  };

  const duplicateQuestionnaire = async (questionnaire) => {
    if (copyingRef.current) return;
    copyingRef.current = true;
    setCopyingId(questionnaire.ID);
    setCopyError("");
    setCopyNotice("");

    try {
      const fieldsSnap = await db
        .collection("QuestionnaireFields")
        .where("CompagnyID", "==", client)
        .where("QuestionnaireID", "==", questionnaire.ID)
        .get();
      const fields = fieldsSnap.docs.map((d) => ({ ...d.data(), docid: d.id }));
      const fieldIds = fields.filter(notDeleted).map((f) => f.ID);

      const [options, rows, columns] = await Promise.all([
        fetchChildren("MultipleQuestionOptions", fieldIds),
        fetchChildren("MatrixQuestionRows", fieldIds),
        fetchChildren("MatrixQuestionColumns", fieldIds),
      ]);

      const copy = buildQuestionnaireCopy({
        questionnaire,
        fields,
        options,
        rows,
        columns,
        client,
        timestamp,
        position: (questionnaires?.length || 0) + 1,
      });

      const writes = [
        ...copy.fields.map((data) => ["QuestionnaireFields", data]),
        ...copy.options.map((data) => ["MultipleQuestionOptions", data]),
        ...copy.rows.map((data) => ["MatrixQuestionRows", data]),
        ...copy.columns.map((data) => ["MatrixQuestionColumns", data]),
        ["Questionnaires", copy.questionnaire],
      ];
      for (let i = 0; i < writes.length; i += COPY_BATCH_SIZE) {
        const batch = db.batch();
        writes
          .slice(i, i + COPY_BATCH_SIZE)
          .forEach(([collection, data]) => batch.set(db.collection(collection).doc(), data));
        await batch.commit();
      }
      setSaved("flex");
      setCopyNotice(standardSkippedNotice(copy.questionnaire.Title, copy.skippedStandard));
    } catch (err) {
      setCopyError(`Kopiëren is niet gelukt: ${err?.message || err}`);
    } finally {
      copyingRef.current = false;
      setCopyingId("");
    }
  };

  // DRAG & DROP — reorder items and update Firestore
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setDragArray(items);

    items.forEach((q, idx) => {
      if (!q?.docid) return;
      db.collection("Questionnaires").doc(q.docid).update({ Position: idx + 1 });
    });

    setSaved("flex");
  };

  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <ListAltOutlinedIcon />
          <h1>Vragenlijsten</h1>
        </div>

        {copyError && <p className="questionnaires-copy-error">{copyError}</p>}
        {copyNotice && <p className="questionnaires-copy-notice">{copyNotice}</p>}

        <ReadOnlyGuard edit="measurement.edit">
          {(canEdit) => (
            <Table
              columns={[
                { key: "drag", label: "", searchable: false },
                { key: "title", label: "TITEL" },
                { key: "research", label: "ONDERZOEK", searchable: false },
                { key: "questions", label: "VRAGEN", searchable: false },
                { key: "duplicate", label: "KOPIËREN", searchable: false },
                { key: "delete", label: "VERWIJDER", searchable: false },
              ]}
              items={dragArray || []}
              getItemKey={(questionnaire) => questionnaire.ID}
              renderRow={(questionnaire) => (
                <>
                  <TableCell>
                    <DragIndicatorOutlinedIcon />
                  </TableCell>
                  <TableTitleCell
                    value={questionnaire.Title}
                    to={`/${client}/questionairebuilder/${questionnaire.ID}`}
                    onBlur={titleHandler}
                    docid={questionnaire.docid}
                    placeholder="Schrijf hier de titel"
                  />
                  <TableCell>
                    {(() => {
                      const titles =
                        researchTitlesByQuestionnaire[questionnaire.ID] || [];
                      if (titles.length === 0) return "-";
                      return (
                        <>
                          {titles.slice(0, 2).join(", ")}
                          {titles.length > 2 && ` +${titles.length - 2}`}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell
                    onClick={() =>
                      history.push(`/${client}/questionairebuilder/${questionnaire.ID}`)
                    }
                  >
                    <FieldCount id={questionnaire.ID} />
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <Tooltip
                        content={
                          subscription === "free"
                            ? "Extra vragenlijsten toevoegen is een premium functie."
                            : "Vragenlijst kopiëren"
                        }
                        top="-60px"
                      >
                        <FileCopyOutlinedIcon
                          data-testid="copy-questionnaire"
                          className={`table-delete-icon${copyingId === questionnaire.ID ? " questionnaires-copy-busy" : ""}`}
                          onClick={subscription === "free" ? undefined : () => duplicateQuestionnaire(questionnaire)}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <DeleteOutlinedIcon data-testid='delete-questionnaire'
                        className="table-delete-icon"
                        data-docid={questionnaire.docid}
                        data-deletename={questionnaire.Title}
                        onClick={deleteModalHandler}
                      />
                    )}
                  </TableCell>
                </>
              )}
              searchAccessor={(questionnaire) => questionnaire?.Title || ""}
              searchPlaceholder="Zoeken op titel"
              emptyMessage="Geen vragenlijsten gevonden."
              draggable
              droppableId="questionnaires"
              onDragEnd={handleDragEnd}
              addRow={canEdit ? {
                toolTipContent:
                  subscription === "free"
                    ? "Extra vragenlijsten toevoegen is een premium functie."
                    : "Vragenlijst toevoegen",
                onClick: subscription === "free" ? null : addQuestionnaire,
              } : undefined}
            />
          )}
        </ReadOnlyGuard>

        <DeleteModal
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
          deleteItem={deleteQuestionnaire}
          deleteName={deleteName} softDelete />
      </div>
    </div>
  );
};

export default Questionnaires;
